import { SECOND } from '@sprytki-frontend-apps/common';
import {
	IAlarmZoneStatusChangedEvent,
	ILockerGateItem,
	ILockerGateStatusChangedEvent,
	IPadLockDetails,
} from 'api/interfaces/lockerGate';
import { LocationServiceAPI } from 'api/services/locationServiceAPI';
import { makeAutoObservable, runInAction } from 'mobx';
import { DictLockerStatus, OpenStatus } from 'shared';

class AlertStore {
	lockerGateItems = new Map<string, ILockerGateItem>();
	isLoading: boolean = false;
	searchPhrase: string = '';

	eventIdToHandle: string | null = null;
	// Returning false means "not the relevant event yet" — keep waiting (e.g. ignore a stale snapshot).
	handleEvent?: (event: ILockerGateStatusChangedEvent) => boolean | void = undefined;

	constructor() {
		makeAutoObservable(this);
	}

	setSearchPhrase = (phrase: string) => {
		this.searchPhrase = phrase;
	};

	clearStore = () => {
		this.lockerGateItems.clear();
		this.eventIdToHandle = null;
		this.handleEvent = undefined;
		this.searchPhrase = '';
	};

	get alerts() {
		return Array.from(this.lockerGateItems.values());
	}

	private setAlertItem = (item: ILockerGateItem) => {
		this.lockerGateItems.set(item.id, item);
	};

	private processEvent = (event: ILockerGateStatusChangedEvent) => {
		if (this.eventIdToHandle !== event.id || !this.handleEvent) return;

		// handleEvent returns false to stay armed (e.g. ignoring a stale snapshot); clear it otherwise.
		if (this.handleEvent(event) !== false) {
			this.eventIdToHandle = null;
			this.handleEvent = undefined;
		}
	};

	onUpdateAlertItem = (
		itemId: string,
		callback: (event: ILockerGateStatusChangedEvent) => boolean | void,
		onTimeoutCallback: () => void,
		timeoutMS: number = 10 * SECOND
	) => {
		this.eventIdToHandle = itemId;
		this.handleEvent = callback;

		setTimeout(() => {
			if (this.eventIdToHandle === itemId) {
				this.eventIdToHandle = null;
				this.handleEvent = undefined;
				onTimeoutCallback && onTimeoutCallback();
			}
		}, timeoutMS);
	};

	updateAlertItem = (event: ILockerGateStatusChangedEvent) => {
		const existing = this.lockerGateItems.get(event.id);
		if (!existing) return;

		const openStatus = event.openStatus === OpenStatus.ReadyToOpen ? OpenStatus.Open : event.openStatus;

		const updated: ILockerGateItem = {
			...existing,
			...event,
			openStatus,
		};

		this.setAlertItem(updated);

		this.processEvent(event);
	};

	private updateLockerItem = (lockerId: string, updateFn: (item: ILockerGateItem) => ILockerGateItem) => {
		const existing = this.lockerGateItems.get(lockerId);
		if (!existing) return;

		const updated = updateFn(existing);
		this.setAlertItem(updated);
	};

	updatePadLockDetails = (lockerId: string, padLockDetails: IPadLockDetails | null) => {
		this.updateLockerItem(lockerId, (existing) => ({
			...existing,
			padLockDetails: padLockDetails ?? null,
		}));
	};

	updateAccessCode = (lockerId: string, lockerAccessCode: string) => {
		this.updateLockerItem(lockerId, (existing) => ({
			...existing,
			lockerAccessCode,
		}));
	};

	updateEmergencyCode = (lockerId: string, emergencyAccessCode: string) => {
		this.updateLockerItem(lockerId, (existing) => ({
			...existing,
			emergencyAccessCode,
		}));
	};

	updateLockerStatus = (lockerId: string, lockerStatus: DictLockerStatus) => {
		this.updateLockerItem(lockerId, (existing) => ({
			...existing,
			lockerStatus,
		}));
	};

	updateOutOfUseReason = (lockerId: string, reason: string | null) => {
		this.updateLockerItem(lockerId, (existing) => ({
			...existing,
			lockerDetails: existing.lockerDetails
				? {
						...existing.lockerDetails,
						outOfUseReason: reason,
					}
				: existing.lockerDetails,
		}));
	};

	updateAlarmZoneState = (event: IAlarmZoneStatusChangedEvent) => {
		const isArmed = event.isArmed ?? undefined;

		// Update the zone item itself.
		this.updateLockerItem(event.id, (existing) => ({
			...existing,
			isArmed,
		}));

		// Cascade the armed state to every locker linked to this Mode-2 zone.
		this.lockerGateItems.forEach((item) => {
			if (item.linkedAlarmZoneId === event.id) {
				this.updateLockerItem(item.id, (existing) => ({
					...existing,
					isArmed,
				}));
			}
		});
	};

	updateOpenStatus = (itemId: string, openStatus: OpenStatus) => {
		const existing = this.lockerGateItems.get(itemId);
		if (!existing) return;

		const updated: ILockerGateItem = {
			...existing,
			openStatus,
			lastOpenedAtUTC: openStatus === OpenStatus.Open ? new Date().toISOString() : existing.lastOpenedAtUTC,
		};

		this.setAlertItem(updated);
	};

	markLockerAsVerified = (lockerId: string) => {
		this.updateLockerItem(lockerId, (existing) => ({
			...existing,
			lockerStatus: DictLockerStatus.Available,
			orderItemId: undefined,
			tenantName: undefined,
			lockerDetails: existing.lockerDetails
				? {
						...existing.lockerDetails,
						tenantDetails: null,
						tenantCompanyDetails: null,
						rentalDetails: null,
					}
				: existing.lockerDetails,
		}));
	};

	fetchAlerts = async (locationId: string) => {
		const isInitialLoad = this.lockerGateItems.size === 0;
		if (isInitialLoad) {
			this.isLoading = true;
		}
		try {
			const response = await LocationServiceAPI.getLockersAndGatesWithAlerts(locationId);
			if (response.success) {
				runInAction(() => {
					const data = response.data;
					const newIds = new Set(data.map((item: ILockerGateItem) => item.id));
					// Remove items not present in the new data
					Array.from(this.lockerGateItems.keys()).forEach((id) => {
						if (!newIds.has(id)) {
							this.lockerGateItems.delete(id);
						}
					});
					// Add/update items from the new data
					data.forEach((item) => this.setAlertItem(item));
				});
			}
		} catch (error) {
			console.error('Error fetching alerts:', error);
		} finally {
			runInAction(() => {
				return (this.isLoading = false);
			});
		}
	};

	updateItemsOpenStatus = () => {
		const nowUtc = new Date();

		this.alerts.forEach((item) => {
			const limitMinutes = item.itemOpenedLimitMinutes;
			if (limitMinutes === null) return;

			if (item.openStatus !== OpenStatus.Open) return;
			if (!item.lastOpenedAtUTC) return;

			const lastOpenedUtc = new Date(item.lastOpenedAtUTC);
			const openDurationInMinutes = (nowUtc.getTime() - lastOpenedUtc.getTime()) / 60000;

			const isOpenTooLong = openDurationInMinutes >= limitMinutes;

			if (!isOpenTooLong) return;

			this.setAlertItem({
				...item,
				openStatus: OpenStatus.OpenTooLong,
			});
		});
	};
}

export const alertStore = new AlertStore();
